import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/endpoints';
import type { Ps5Rate } from '../api/types';
import { formatMinutes, formatMoney } from '../lib/format';
import { ErrorNote, Spinner } from './ui';

export type Ps5Choice = ReturnType<typeof usePs5Rates>;

// A PS5 is sold as players × duration at a flat price from the rate card.
// Shared by both ways of starting one — from the station, and from the
// customer — so the two cannot drift. `enabled` false skips the fetch for a
// dialog that is not (yet) seating anyone on a PS5.
export function usePs5Rates(enabled: boolean) {
  const [rates, setRates] = useState<Ps5Rate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [players, setPlayers] = useState<number | null>(null);
  const [duration, setDuration] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled || rates !== null) return;
    const controller = new AbortController();
    api.recharges
      .pricing(controller.signal)
      .then((pricing) => setRates(pricing.ps5_rates))
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      });
    return () => controller.abort();
  }, [enabled, rates]);

  const rate =
    rates?.find(
      (option) => option.player_count === players && option.duration_minutes === duration,
    ) ?? null;

  return { rates, error, players, setPlayers, duration, setDuration, rate };
}

export function Ps5RatePicker({ choice }: { choice: Ps5Choice }) {
  const { rates, error, players, setPlayers, duration, setDuration, rate } = choice;

  const playerOptions = useMemo(
    () => [...new Set((rates ?? []).map((entry) => entry.player_count))].sort((a, b) => a - b),
    [rates],
  );
  const durationOptions = useMemo(
    () =>
      [...new Set((rates ?? []).map((entry) => entry.duration_minutes))].sort((a, b) => a - b),
    [rates],
  );

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (rates === null) return <Spinner label="Loading PS5 rates…" />;
  if (rates.length === 0) {
    return <ErrorNote>No PS5 rates are on the rate card, so a session cannot be priced.</ErrorNote>;
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1.5 text-sm font-medium text-slate-300">Players</p>
        <div className="grid grid-cols-4 gap-2">
          {playerOptions.map((count) => (
            <button
              key={count}
              type="button"
              onClick={() => setPlayers(count)}
              className={`min-h-11 rounded-lg border text-sm font-medium transition-colors ${
                players === count
                  ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                  : 'border-slate-800 text-slate-300 hover:bg-slate-800'
              }`}
            >
              {count}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-1.5 text-sm font-medium text-slate-300">How long</p>
        <div className="grid grid-cols-2 gap-2">
          {durationOptions.map((minutes) => {
            const option = rates.find(
              (entry) => entry.player_count === players && entry.duration_minutes === minutes,
            );
            return (
              <button
                key={minutes}
                type="button"
                onClick={() => setDuration(minutes)}
                disabled={players !== null && !option}
                className={`min-h-11 rounded-lg border px-2 text-sm font-medium transition-colors disabled:opacity-40 ${
                  duration === minutes
                    ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                    : 'border-slate-800 text-slate-300 hover:bg-slate-800'
                }`}
              >
                {formatMinutes(minutes)}
                {option && <span className="ml-2 text-slate-400">{formatMoney(option.price)}</span>}
              </button>
            );
          })}
        </div>
      </div>
      {rate && (
        <p className="text-sm text-slate-300">
          {rate.player_count} player{rate.player_count === 1 ? '' : 's'} ·{' '}
          {formatMinutes(rate.duration_minutes)} ·{' '}
          <span className="font-semibold text-slate-100">{formatMoney(rate.price)}</span> on the
          payer's tab
        </p>
      )}
    </div>
  );
}
